<script setup>
import { reactive, computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength, requiredIf, url } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_MARKDOWN_CONTENT_LENGTH = 200000;

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainDocuments/getUIFlags'),
};

const initialState = {
  name: '',
  url: '',
  documentType: 'url',
  pdfFiles: [],
  markdownFiles: [],
};

const state = reactive({ ...initialState });
const fileInputRef = ref(null);

const validationRules = {
  url: {
    required: requiredIf(() => state.documentType === 'url'),
    url: requiredIf(() => state.documentType === 'url' && url),
    minLength: requiredIf(() => state.documentType === 'url' && minLength(1)),
  },
  pdfFiles: {
    required: requiredIf(() => state.documentType === 'pdf'),
  },
  markdownFiles: {
    required: requiredIf(() => state.documentType === 'markdown'),
  },
};

const documentTypeOptions = [
  { value: 'url', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.URL') },
  { value: 'pdf', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.PDF') },
  { value: 'markdown', label: t('CAPTAIN.DOCUMENTS.FORM.TYPE.MARKDOWN') },
];

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const hasPdfFileError = computed(() => v$.value.pdfFiles.$error);
const hasMarkdownFileError = computed(() => v$.value.markdownFiles.$error);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.DOCUMENTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  url: getErrorMessage('url', 'URL'),
  pdfFiles: getErrorMessage('pdfFiles', 'PDF_FILE'),
  markdownFiles: getErrorMessage('markdownFiles', 'MARKDOWN_FILE'),
}));

const handleCancel = () => emit('cancel');

const resetSelectedFiles = () => {
  state.pdfFiles = [];
  state.markdownFiles = [];
  state.name = '';
};

const selectedFileCount = computed(() => {
  if (state.documentType === 'pdf') return state.pdfFiles.length;
  if (state.documentType === 'markdown') return state.markdownFiles.length;
  return 0;
});

const canEditFileDocumentName = computed(() => selectedFileCount.value <= 1);

const formatTotalSize = files => {
  const totalBytes = files.reduce((total, file) => total + file.size, 0);
  if (totalBytes >= 1024 * 1024) {
    return `${(totalBytes / 1024 / 1024).toFixed(2)} MB`;
  }
  return `${(totalBytes / 1024).toFixed(1)} KB`;
};

const fileSelectionLabel = (files, emptyLabelKey) => {
  if (!files.length) return t(emptyLabelKey);
  if (files.length === 1) return files[0].name;
  return t('CAPTAIN.DOCUMENTS.FORM.FILE_SELECTION.COUNT', {
    count: files.length,
  });
};

const fileSelectionHelpText = (files, emptyHelpKey) => {
  if (!files.length) return t(emptyHelpKey);
  return files.length === 1
    ? formatTotalSize(files)
    : t('CAPTAIN.DOCUMENTS.FORM.FILE_SELECTION.TOTAL_SIZE', {
        size: formatTotalSize(files),
      });
};

const handleFileChange = event => {
  const files = [...event.target.files];
  if (files.length) {
    const invalidFile = files.find(
      file => file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)
    );
    if (invalidFile) {
      resetSelectedFiles();
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.INVALID_TYPE'));
      event.target.value = '';
      return;
    }

    const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE);
    if (oversizedFile) {
      resetSelectedFiles();
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.TOO_LARGE'));
      event.target.value = '';
      return;
    }

    state.pdfFiles = files;
    state.name =
      files.length === 1 ? files[0].name.replace(/\.pdf$/i, '') : '';
  }
};

const isMarkdownFile = file => /\.(md|markdown)$/i.test(file.name);

const handleMarkdownFileChange = async event => {
  const files = [...event.target.files];
  if (files.length) {
    const invalidFile = files.find(file => !isMarkdownFile(file));
    if (invalidFile) {
      resetSelectedFiles();
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.INVALID_TYPE'));
      event.target.value = '';
      return;
    }

    const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE);
    if (oversizedFile) {
      resetSelectedFiles();
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.TOO_LARGE'));
      event.target.value = '';
      return;
    }

    const fileTexts = await Promise.all(files.map(file => file.text()));
    const emptyFileIndex = fileTexts.findIndex(text => !text.trim());
    if (emptyFileIndex >= 0) {
      resetSelectedFiles();
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.EMPTY'));
      event.target.value = '';
      return;
    }

    const oversizedContentIndex = fileTexts.findIndex(
      text => text.length > MAX_MARKDOWN_CONTENT_LENGTH
    );
    if (oversizedContentIndex >= 0) {
      resetSelectedFiles();
      useAlert(t('CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.CONTENT_TOO_LARGE'));
      event.target.value = '';
      return;
    }

    state.markdownFiles = files;
    state.name =
      files.length === 1 ? files[0].name.replace(/\.(md|markdown)$/i, '') : '';
  }
};

const openFileDialog = () => {
  nextTick(() => {
    fileInputRef.value?.click();
  });
};

const prepareFileDocumentDetails = (fieldName, files, extensionPattern) => {
  return files.map(file => {
    const formData = new FormData();
    formData.append('document[assistant_id]', props.assistantId);
    formData.append(`document[${fieldName}]`, file);
    formData.append(
      'document[name]',
      files.length === 1 && state.name
        ? state.name
        : file.name.replace(extensionPattern, '')
    );
    return formData;
  });
};

const prepareDocumentDetails = () => {
  const formData = new FormData();
  formData.append('document[assistant_id]', props.assistantId);

  if (state.documentType === 'url') {
    formData.append('document[external_link]', state.url);
    formData.append('document[name]', state.name || state.url);
    return formData;
  }

  if (state.documentType === 'pdf') {
    return prepareFileDocumentDetails('pdf_file', state.pdfFiles, /\.pdf$/i);
  }

  return prepareFileDocumentDetails(
    'markdown_file',
    state.markdownFiles,
    /\.(md|markdown)$/i
  );
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareDocumentDetails());
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label
        for="documentType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAPTAIN.DOCUMENTS.FORM.TYPE.LABEL') }}
      </label>
      <ComboBox
        id="documentType"
        v-model="state.documentType"
        :options="documentTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="resetSelectedFiles"
      />
    </div>

    <Input
      v-if="state.documentType === 'url'"
      v-model="state.url"
      :label="t('CAPTAIN.DOCUMENTS.FORM.URL.LABEL')"
      :placeholder="t('CAPTAIN.DOCUMENTS.FORM.URL.PLACEHOLDER')"
      :message="formErrors.url"
      :message-type="formErrors.url ? 'error' : 'info'"
    />

    <div v-if="state.documentType === 'pdf'" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.DOCUMENTS.FORM.PDF_FILE.LABEL') }}
      </label>
      <div class="relative">
        <input
          ref="fileInputRef"
          type="file"
          accept=".pdf"
          multiple
          class="hidden"
          @change="handleFileChange"
        />
        <Button
          type="button"
          :color="hasPdfFileError ? 'ruby' : 'slate'"
          :variant="hasPdfFileError ? 'outline' : 'solid'"
          class="!w-full !h-auto !justify-between !py-4"
          @click="openFileDialog"
        >
          <template #default>
            <div class="flex gap-2 items-center min-w-0">
              <div
                class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-slate-3 shrink-0"
              >
                <i class="text-xl i-ph-file-pdf text-n-slate-11" />
              </div>
              <div class="flex flex-col flex-1 gap-1 items-start min-w-0">
                <p
                  class="m-0 w-full text-sm font-medium truncate text-n-slate-12"
                >
                  {{
                    fileSelectionLabel(
                      state.pdfFiles,
                      'CAPTAIN.DOCUMENTS.FORM.PDF_FILE.CHOOSE_FILE'
                    )
                  }}
                </p>
                <p class="m-0 text-xs text-n-slate-11">
                  {{
                    fileSelectionHelpText(
                      state.pdfFiles,
                      'CAPTAIN.DOCUMENTS.FORM.PDF_FILE.HELP_TEXT'
                    )
                  }}
                </p>
              </div>
            </div>

            <i class="i-lucide-upload text-n-slate-11" />
          </template>
        </Button>
      </div>
      <p v-if="formErrors.pdfFiles" class="text-xs text-n-ruby-9">
        {{ formErrors.pdfFiles }}
      </p>
    </div>

    <div
      v-if="state.documentType === 'markdown'"
      class="flex flex-col gap-2"
    >
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.LABEL') }}
      </label>
      <div class="relative">
        <input
          ref="fileInputRef"
          type="file"
          accept=".md,.markdown"
          multiple
          class="hidden"
          @change="handleMarkdownFileChange"
        />
        <Button
          type="button"
          :color="hasMarkdownFileError ? 'ruby' : 'slate'"
          :variant="hasMarkdownFileError ? 'outline' : 'solid'"
          class="!w-full !h-auto !justify-between !py-4"
          @click="openFileDialog"
        >
          <template #default>
            <div class="flex gap-2 items-center min-w-0">
              <div
                class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-slate-3 shrink-0"
              >
                <i class="text-xl i-lucide-file-text text-n-slate-11" />
              </div>
              <div class="flex flex-col flex-1 gap-1 items-start min-w-0">
                <p
                  class="m-0 w-full text-sm font-medium truncate text-n-slate-12"
                >
                  {{
                    fileSelectionLabel(
                      state.markdownFiles,
                      'CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.CHOOSE_FILE'
                    )
                  }}
                </p>
                <p class="m-0 text-xs text-n-slate-11">
                  {{
                    fileSelectionHelpText(
                      state.markdownFiles,
                      'CAPTAIN.DOCUMENTS.FORM.MARKDOWN_FILE.HELP_TEXT'
                    )
                  }}
                </p>
              </div>
            </div>

            <i class="i-lucide-upload text-n-slate-11" />
          </template>
        </Button>
      </div>
      <p v-if="formErrors.markdownFiles" class="text-xs text-n-ruby-9">
        {{ formErrors.markdownFiles }}
      </p>
    </div>

    <Input
      v-model="state.name"
      :label="t('CAPTAIN.DOCUMENTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.DOCUMENTS.FORM.NAME.PLACEHOLDER')"
      :disabled="!canEditFileDocumentName"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('CAPTAIN.FORM.CREATE')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
